/**
 * Session + entitlement context.
 *
 * The tier comes from the ENTITLEMENT ENDPOINT, which reads the state the billing webhook
 * recorded — never from the auth session, and never from anything the browser can set
 * (BUSINESS_RULES § auth rules 1 and 5). Being signed in says who you are; it says nothing
 * about what you have paid for.
 *
 * This context decides what the NAVIGATION offers. It is not a security boundary and must
 * never be treated as one: the API validates the token and re-checks entitlement on every
 * request, which is the only check that actually holds. What this prevents is a UI that
 * advertises surfaces the account cannot use.
 */
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useKindeAuth } from '@kinde-oss/kinde-auth-react';
import { useQuery } from '@tanstack/react-query';
import { fetchAccount } from '@/lib/api';
import type { Tier } from '@/lib/tier';
import { TIERS } from '@/lib/tier';

export interface Session {
  tier: Tier;
  /** True until the entitlement is known. Nav renders its lowest-privilege shape meanwhile. */
  loading: boolean;
  /**
   * Signed in, but no subscription on record. Distinct from `loading` — this is a settled
   * answer, and it means the person needs to choose a plan before the product will serve them.
   */
  needsPlan: boolean;
  /**
   * Development affordance for previewing the nav at each tier. A no-op in production builds —
   * see the override below.
   */
  setTier: (tier: Tier) => void;
}

const SessionContext = createContext<Session | null>(null);

const STORAGE_KEY = 'aware.dev.tier';

/**
 * The tier override is a DEV-ONLY affordance and is compiled out of production.
 *
 * It reads `?tier=` and localStorage, both of which the visitor controls. Left enabled in
 * production it would let anyone render the Pro navigation — not a data leak, since the API
 * decides every read, but a UI that promises surfaces the account has not bought and then
 * fails on each one. `import.meta.env.DEV` is statically false in a build, so the branch is
 * removed entirely rather than merely skipped.
 */
const ALLOW_TIER_OVERRIDE = import.meta.env.DEV;

function isTier(value: unknown): value is Tier {
  return value === 'snapshot' || value === 'intelligence' || value === 'pro';
}

function devTier(): Tier | null {
  if (!ALLOW_TIER_OVERRIDE) return null;
  const fromQuery = new URLSearchParams(window.location.search).get('tier');
  if (isTier(fromQuery)) {
    window.localStorage.setItem(STORAGE_KEY, fromQuery);
    return fromQuery;
  }
  const stored = window.localStorage.getItem(STORAGE_KEY);
  return isTier(stored) ? stored : null;
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useKindeAuth();
  const [override, setOverride] = useState<Tier | null>(devTier);

  // Shares `['account']` with the Account screen, so the entitlement is fetched once and both
  // read the same cached answer.
  const { data: account, isPending } = useQuery({
    queryKey: ['account'],
    queryFn: fetchAccount,
    enabled: isAuthenticated,
    staleTime: 60_000,
    retry: false,
  });

  const setTier = useCallback((next: Tier) => {
    if (!ALLOW_TIER_OVERRIDE) return;
    window.localStorage.setItem(STORAGE_KEY, next);
    setOverride(next);
  }, []);

  const value = useMemo<Session>(() => {
    // Defaults to the LOWEST tier while unknown, never the highest. An account that briefly
    // renders Pro and then collapses to Snapshot looks like something was taken away.
    const tier: Tier = override ?? account?.tier ?? 'snapshot';
    const settled = isAuthenticated && !isPending;
    return {
      tier,
      loading: isAuthenticated && isPending,
      // Only once the answer has actually arrived. Treating "still loading" as "needs a plan"
      // would flash the plan chooser at a paying subscriber on every cold load.
      needsPlan: settled && account?.tier == null && override === null,
      setTier,
    };
  }, [override, account?.tier, isAuthenticated, isPending, setTier]);

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): Session {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error('useSession must be used inside <SessionProvider>');
  return ctx;
}

export function useTier(): Tier {
  return useSession().tier;
}

export function useTierDefinition() {
  return TIERS[useTier()];
}
